import PageTitle from "../PageTitle";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  FormControlLabel,
  FormGroup,
  IconButton,
  Switch,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EnhancedTable from "../../layouts/table";
import { useContext, useEffect, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import FilterForm from "./FilterForm";
import RoleModal from "./RoleModal";
import axios from "axios";
import { debounce } from "lodash";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import toast from "react-hot-toast";
import { AppContext } from "../../scripts/context/App";
import LoadingTableRows from "../LoadingTableRows";

interface IFilter {
  status: boolean;
  data: {
    name: string;
    status: string;
  };
}
const initFilterData: IFilter = {
  status: true,
  data: {
    name: "",
    status: "",
  },
};

interface INewRole {
  status: boolean;
  edit: boolean;
  data: {
    name: string;
    status: boolean;
    permissions: string[];
  };
}

const initRoleState: INewRole = {
  status: false,
  edit: false,
  data: {
    name: "",
    status: true,
    permissions: [],
  },
};

export default function RolesManagement() {
  const { user, roles_permissions, setState } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [tableData, setTableDatas] = useState({
    page: 1,
    pagesCount: 10,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    tableHead: [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "users_count",
        numeric: false,
        disablePadding: false,
        label: "Assigned User",
      },
      {
        id: "status",
        numeric: false,
        disablePadding: false,
        label: "Status",
      },
      {
        id: "actions",
        numeric: false,
        disablePadding: false,
        label: "Actions",
      },
    ],
    tableItems: [],
  });
  const setTableData = (data: any) => setTableDatas({ ...tableData, ...data });

  const [filter, setFilter] = useState<IFilter>(initFilterData);
  const [role, setRole] = useState<INewRole>(initRoleState);
  const [deleteItem, setDeleteItem] = useState({
    status: false,
    name: "",
    data: {
      role_id: "",
      alternative_role_id: "",
    },
  });
  const [order, setOrder] = useState<any>({
    order: "asc",
    orderBy: "name",
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTableData({
      rowsPerPage: Number(event.target.value),
      page: 1,
    });

    getRoles({
      name: filter.data.name,
      status: filter.data.status,
      sortBy: order.order,
      sortOrder: order.orderBy,
      page: 1,
      perPage: event.target.value,
    });
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    getRoles({
      name: filter.data.name,
      status: filter.data.status,
      sortBy: order.order,
      sortOrder: order.orderBy,
      page: newPage,
      perPage: tableData.rowsPerPage,
    });

    setTableData({ page: newPage });
  };

  const getRoles = async (params?: any) => {
    try {
      const {
        data: { data },
      } = await axios.get("/roles/get-roles", { params });

      setLoading(false);
      setTableData({
        tableItems: data.data,
        pagesCount: data.total,
        rowsPerPage: data.per_page,
        page: data.current_page,
      });
    } catch (error: any) {
      setLoading(false);

      toast.error(
        error.response.data.message || "Something went wrong. Please try again later or contact support."
      );
      console.log(error);
    }
  };

  useEffect(() => {
    if (!user.permissions.includes("view_system_role")) return;
    getRoles();
  }, []);

  const changeStatus = debounce(async (role_id) => {
    if (!user.permissions.includes("active_inactive_system_role")) return toast.error(
      "You dont have permissions to do this action"
    );

    try {
      await axios.post("/roles/change-status", { role_id });

      setState({
        roles_permissions: roles_permissions.map((role: any) => {
          if (role.id === role_id) {
            role.status = role.status === "Active" ? "Inactive" : "Active";
          }

          return role;
        })
      });
    } catch (error: any) {
      console.log(error);

      toast.error(
        error.response.data.message || "Something went wrong. Please try again later or contact support."
      )
    }
  }, 800);

  const deleteRow = async () => {
    if (!user.permissions.includes("delete_system_role")) return toast.error("You dont have permissions to do this action");

    if (!deleteItem.data.alternative_role_id)
      return toast.error("Please select new role");

    setDeleteLoading(true);

    try {
      await axios.delete("/roles/delete", {
        data: deleteItem.data,
      });

      setTableData({
        tableItems: tableData.tableItems.filter(
          (item: any) => item.id !== deleteItem.data.role_id
        ),
      });
      setDeleteItem({
        status: false,
        name: "",
        data: {
          role_id: "",
          alternative_role_id: "",
        },
      });
      setState({
        roles_permissions: roles_permissions.filter(
          (item: any) => item.id !== deleteItem.data.role_id
        ),
      });
      setDeleteLoading(false);

      toast.success("Role deleted successfully");
    } catch (error) {
      setDeleteLoading(false);
      toast.error("Something went wrong when deleting role");

      console.log(error);
    }
  };

  if (!user.permissions.includes("view_system_role")) return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <h3>You dont have permissions to view this page</h3>
    </Box>
  )

  return (
    <>
      <PageTitle
        title="Roles Management"
        description="Manage your roles"
        actionLabel="Add new role"
        action={() => {
          if (!user.permissions.includes("add_system_role")) return toast.error("You dont have permissions to do this action");

          setRole({ ...role, status: true, edit: false });
        }}
        startIcon={<AddIcon />}
      />
      <RoleModal
        data={role}
        getRoles={getRoles}
        setData={(object: any) =>
          setRole({
            ...role,
            data: { ...role.data, ...object },
          })
        }
        removeAssignedUsers={() => {
          const tempTableItems = tableData.tableItems;
          tempTableItems.forEach((item: any) => {
            if (item.name === role.data.name) {
              item.users = [];
            }
          });
          setTableData({ ...tableData, tableItems: tempTableItems });
        }}
        handleClose={() => {
          setRole(initRoleState);
        }}
      />
      <ConfirmDeleteModal
        data={deleteItem}
        deleteRow={() => {
          if (!user.permissions.includes("delete_system_role")) return toast.error("You dont have permissions to do this action");

          deleteRow();
        }}
        setData={(object: any) => setDeleteItem({ ...deleteItem, ...object })}
        isLoading={deleteLoading}
        setIsLoading={setDeleteLoading}
      />
      <Box sx={{ p: 3 }}>
        <EnhancedTable
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          handleRequestSortHeader={(order, orderBy) => {
            if (!orderBy) return;

            getRoles({
              name: filter.data.name,
              status: filter.data.status,
              sortBy: orderBy,
              sortOrder: order,
              page: 1,
              perPage: tableData.rowsPerPage,
            });

            setOrder({ order, orderBy });
          }}
          TableRows={
            loading ? (
              <LoadingTableRows elementsCount={tableData.tableHead.length} />
            ) : tableData.tableItems.length > 0 ? (
              tableData.tableItems.map((row: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.users_count}</TableCell>
                  <TableCell>
                    <FormGroup
                      sx={{ maxWidth: "fit-content", justifyContent: "center" }}
                    >
                      <FormControlLabel
                        control={<Switch checked={row.status === "Active"} />}
                        label={row.status === "Active" ? "Active" : "Inactive"}
                        onChange={() => {
                          if (!user.permissions.includes("active_inactive_system_role")) return toast.error(
                            "You dont have permissions to do this action"
                          )
                          changeStatus(row.id);

                          const newTableData = {
                            ...tableData,
                            tableItems: tableData.tableItems.map(
                              (item: any) => {
                                if (item.id === row.id) {
                                  return {
                                    ...item,
                                    status:
                                      row.status === "Active"
                                        ? "Inactive"
                                        : "Active",
                                  };
                                }
                                return item;
                              }
                            ),
                          };
                          setTableData(newTableData);
                        }}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          if (!user.permissions.includes("edit_system_role")) return toast.error("You dont have permissions to do this action");

                          setRole({ status: true, edit: true, data: row });
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      color="error"
                      onClick={() => {
                        if (!user.permissions.includes("delete_system_role")) return toast.error("You dont have permissions to do this action");

                        setDeleteItem({
                          status: !deleteItem.status,
                          name: row.name,
                          data: {
                            role_id: row.id,
                            alternative_role_id: "",
                          },
                        });
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={tableData.tableHead.length}>
                  <Typography
                    sx={{ textAlign: "center" }}
                    variant="body2"
                    color="textPrimary"
                  >
                    No data found
                  </Typography>
                </TableCell>
              </TableRow>
            )
          }
          {...tableData}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() =>
                  setFilter({
                    ...filter,
                    status: !filter.status,
                  })
                }
              >
                {filter.status ? <FilterListIcon /> : <FilterListOffIcon />}
              </IconButton>
              <Typography variant="h6">Filter by</Typography>
            </Box>
            {filter.status && (
              <FilterForm
                filter={filter}
                setFilter={setFilter}
                handleSearch={(data) => {
                  if (data?.data) {
                    getRoles({
                      ...data.data,
                      sortBy: order.orderBy,
                      sortOrder: order.order,
                    });
                  } else {
                    getRoles({
                      ...filter.data,
                      sortBy: order.orderBy,
                      sortOrder: order.order,
                    });
                  }
                }}
              />
            )}
          </Box>
        </EnhancedTable>
      </Box>
    </>
  );
}
